import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Box, Typography } from '@mui/material';

import { useTranslation } from 'react-i18next';

import SideNav from './components/SideNav';
import Toolbar, { ViewMode } from './components/Toolbar';
import AddressBar from './components/AddressBar';
import FileBrowser from './components/FileBrowser';
import FileContextMenu, { CtxAction } from './components/FileContextMenu';
import NewFolderDialog from './components/NewFolderDialog';
import RenameDialog from './components/RenameDialog';
import DeleteConfirmDialog from './components/DeleteConfirmDialog';
import ShareDialog from '../driveShare/ShareDialog';
import FilePreviewModal from './components/FilePreviewModal';
import FolderTreePicker from './components/FolderTreePicker';
import BatchDeleteDialog from './components/BatchDeleteDialog';
import WebDAVSettingsDialog from './components/WebDAVSettingsDialog';
import FileUploader from './components/FileUploader';
import myDriveService, { uploadFolderFiles, isOfficeDoc } from './services/myDriveService';
import NewOfficeFileDialog, { OfficeFileType } from './components/NewOfficeFileDialog';

import { useDriveFiles } from './hooks/useDriveFiles';
import { useCategoryFolders } from './hooks/useCategoryFolders';
import { useQuota } from './hooks/useQuota';
import { useFileSelection } from './hooks/useFileSelection';
import { useFileDragDrop } from './hooks/useFileDragDrop';

import { DirCategory, DriveFile } from './services/myDriveService';
import { usePermission } from '../../../hooks/usePermission';
import { useMessage } from '../../../contexts/MessageContext';

const READONLY_CATS = new Set<DirCategory>(['system', 'mail']);

export default function MyDrivePage() {
  const { t } = useTranslation();

  const { hasPermission, loading: permLoading } = usePermission();
  const canRead   = hasPermission('ee_my_drive', 'read');
  const canCreate = hasPermission('ee_my_drive', 'create');
  const canUpdate = hasPermission('ee_my_drive', 'update');
  const canDelete = hasPermission('ee_my_drive', 'delete');
  const canShare  = hasPermission('ee_drive_share', 'create');   // 分享走獨立 func（免 click→403 誤會系統壞）

  const [category, setCategory] = useState<DirCategory>('personal');
  const [prefix, setPrefix]     = useState('');
  const [view, setView]         = useState<ViewMode>('list');
  const readonly = READONLY_CATS.has(category);

  const { files, loading, load, remove, createFolder, rename, move } = useDriveFiles(category);
  const { folders: allCategoryFolders, reload: reloadFolders } = useCategoryFolders(category);
  const { quota, reload: reloadQuota } = useQuota();

  // Explorer 模式：只顯示當前 prefix 的直下子項目
  // list_files 回傳所有階層，需前端 filter 只留直接子代
  const directFiles = files.filter(f => {
    const remaining = f.relative_path.slice(prefix.length);
    return remaining.length > 0 && !remaining.includes('/');
  });

  const { selected, onItemClick, clear } = useFileSelection(directFiles);

  // hidden file input for toolbar upload
  const uploadRef = useRef<HTMLInputElement>(null);
  // 資料夾上傳（階段 1）：webkitdirectory input + 進度 + 總結
  const folderInputRef = useRef<HTMLInputElement>(null);
  const [folderProgress, setFolderProgress] = useState<{ done: number; total: number } | null>(null);
  const [folderSummary, setFolderSummary] = useState<{ ok: number; fail: number } | null>(null);
  const [uploaderKey, setUploaderKey] = useState(0);

  useEffect(() => { load(prefix); clear(); }, [category, prefix]); // eslint-disable-line
  useEffect(() => { reloadQuota(); }, []); // eslint-disable-line

  // ── dialogs ──────────────────────────────────────────────────────────────
  const [newFolderOpen,    setNewFolderOpen]    = useState(false);
  const [newOfficeOpen,    setNewOfficeOpen]    = useState(false);
  const [renameTarget,     setRenameTarget]     = useState<DriveFile | null>(null);
  const [deleteTarget,     setDeleteTarget]     = useState<DriveFile | null>(null);
  const [moveTarget,       setMoveTarget]       = useState<DriveFile | null>(null);
  const [shareTarget,      setShareTarget]      = useState<DriveFile | null>(null);   // #24 分享
  const [batchDeleteOpen,  setBatchDeleteOpen]  = useState(false);
  const [batchMoveOpen,    setBatchMoveOpen]    = useState(false);
  const [webdavOpen,       setWebdavOpen]       = useState(false);
  const [webdavEnabled,    setWebdavEnabled]    = useState<boolean | null>(null);
  const [previewFile,      setPreviewFile]      = useState<DriveFile | null>(null);

  const refreshWebdavStatus = useCallback(async () => {
    try {
      const info = await myDriveService.getWebdavInfo();
      setWebdavEnabled(info.enabled);
    } catch {
      setWebdavEnabled(false);
    }
  }, []);

  useEffect(() => { refreshWebdavStatus(); }, [refreshWebdavStatus]);

  const selectedFiles = useMemo(
    () => directFiles.filter(f => selected.has(f.id)),
    [directFiles, selected]
  );
  const [ctxPos,       setCtxPos]             = useState<{ top: number; left: number } | null>(null);
  const [ctxFile,      setCtxFile]            = useState<DriveFile | null>(null);
  const { showSuccess, showApiError } = useMessage();

  const openCtx = (ev: React.MouseEvent, file: DriveFile | null) => {
    ev.preventDefault();
    setCtxPos({ top: ev.clientY, left: ev.clientX });
    setCtxFile(file);
  };

  const handleDownload = async (file: DriveFile) => {
    try {
      await myDriveService.downloadFile(file.id, file.filename);
      // silent ok: 瀏覽器下載對話框即自明回饋，單檔無需 toast
    } catch (err) {
      showApiError(err);
    }
  };

  const handleBatchDownload = async () => {
    if (selected.size === 0) return;
    try {
      await myDriveService.downloadBatch(Array.from(selected));
      showSuccess('SYS330013', { count: String(selected.size) });
    } catch (err) {
      showApiError(err);
    }
  };

  // 線上編輯：先 promote 成 file_object（fast/slow path）→ 新分頁開 OfficeEditorPage
  // （noopener,noreferrer → 新分頁 window.opener=null，防 reverse tabnabbing）
  const handleEditOnline = async (file: DriveFile) => {
    try {
      const resp = await myDriveService.promoteToEditable(file.id);
      // Sprint 4 · 帶 name/mime 供 AI 面板 context
      const q = `?name=${encodeURIComponent(file.filename)}&mime=${encodeURIComponent(file.mime_type)}`;
      window.open(`/office/${resp.file_object_id}${q}`, '_blank', 'noopener,noreferrer');
    } catch (err) {
      showApiError(err);
    }
  };

  // 3.2c 新建空白 Office 檔 → 立即新分頁開 Collabora 編輯 + refresh list
  const handleCreateOfficeFile = async (fileType: OfficeFileType, filename: string) => {
    try {
      const resp = await myDriveService.createNewOfficeFile({
        file_type: fileType, filename, dir_category: category, relative_path: prefix,
      });
      const officeMime: Record<OfficeFileType, string> = {
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      };
      const q = `?name=${encodeURIComponent(`${filename}.${fileType}`)}&mime=${encodeURIComponent(officeMime[fileType])}`;
      window.open(`/office/${resp.file_object_id}${q}`, '_blank', 'noopener,noreferrer');
      load(prefix); reloadQuota(); reloadFolders();
    } catch (err) {
      showApiError(err);
    }
  };

  // 3.2c BUG FIX 2：Office 文件（docx/xlsx/pptx）檢視 → Collabora view mode（browser 無法 iframe render）；
  // pdf/圖片等維持現況 modal preview。
  const handlePreview = async (file: DriveFile) => {
    if (isOfficeDoc(file.mime_type)) {
      try {
        const resp = await myDriveService.promoteToEditable(file.id);
        window.open(`/office/${resp.file_object_id}?mode=view`, '_blank', 'noopener,noreferrer');
      } catch (err) {
        showApiError(err);
      }
      return;
    }
    setPreviewFile(file);
  };

  const handleCtxAction = async (action: CtxAction) => {
    if (action === 'preview' && ctxFile) handlePreview(ctxFile);
    if (action === 'download' && ctxFile) handleDownload(ctxFile);
    if (action === 'edit-online' && ctxFile) handleEditOnline(ctxFile);
    if (action === 'rename'  && ctxFile) setRenameTarget(ctxFile);
    if (action === 'delete'  && ctxFile) setDeleteTarget(ctxFile);
    if (action === 'move'    && ctxFile) setMoveTarget(ctxFile);
    if (action === 'share'   && ctxFile) setShareTarget(ctxFile);
    if (action === 'newFolder')          setNewFolderOpen(true);
    if (action === 'upload')             uploadRef.current?.click();
    if (action === 'copy' && ctxFile) {
      try {
        const newFile = await myDriveService.copyFile(ctxFile.id);
        showSuccess('SYS330008', { name: newFile.filename });
        load(prefix);
        reloadQuota();
      } catch (err) {
        showApiError(err);
      }
    }
  };

  const handleBatchDelete = async (recursive: boolean) => {
    try {
      const result = await myDriveService.batchDelete(Array.from(selected), recursive);
      showSuccess('SYS330009', { count: String(result.success_count) });
      clear();
      setBatchDeleteOpen(false);
      load(prefix);
      reloadQuota();
      reloadFolders();   // 可能刪到 folder → SideNav 樹同步
    } catch (err) {
      showApiError(err);
    }
  };

  const handleBatchMove = async (targetDir: string) => {
    try {
      const result = await myDriveService.batchMove(Array.from(selected), targetDir);
      showSuccess('SYS330010', { count: String(result.success_count) });
      clear();
      setBatchMoveOpen(false);
      load(prefix);
      reloadQuota();
      reloadFolders();   // 可能移到 folder → SideNav 樹同步
    } catch (err) {
      showApiError(err);
    }
  };

  const handleDoubleClick = (file: DriveFile) => {
    if (file.is_directory) {
      setPrefix(file.relative_path + '/');
      clear();
    } else {
      // 3.2d bug3：reuse handlePreview 分流（Office → Collabora view 新分頁；非 Office → modal）。
      // 直接 setPreviewFile 會讓 Office mime 走 FilePreviewModal iframe blob → browser 觸發下載且檔名錯。
      handlePreview(file);
    }
  };

  // ── upload (manual trigger) ───────────────────────────────────────────────
  const handleUploadFiles = useCallback(async (fileList: FileList) => {
    // 觸發 FileUploader 的 hidden input，靠 key 換新 instance 清空狀態
    setUploaderKey(k => k + 1);
    // 用 DataTransfer 轉成 change event 觸發
    if (uploadRef.current) {
      const dt = new DataTransfer();
      Array.from(fileList).forEach(f => dt.items.add(f));
      uploadRef.current.files = dt.files;
      uploadRef.current.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }, []);

  // 資料夾上傳：解析結構 → 建 subfolder → 逐檔上傳（某檔失敗續傳），進度 + 總結
  const handleFolderUpload = useCallback(async (fileList: FileList | null) => {
    const files = fileList ? Array.from(fileList) : [];
    if (files.length === 0) return;
    setFolderSummary(null);
    setFolderProgress({ done: 0, total: files.length });
    try {
      const { success, failed } = await uploadFolderFiles(
        category, prefix, files, (done, total) => setFolderProgress({ done, total }));
      setFolderSummary({ ok: success, fail: failed.length });
      if (failed.length === 0) showSuccess('SYS330008', { name: `${success}` });
      load(prefix); reloadQuota(); reloadFolders();
    } catch (err) {
      showApiError(err);
    } finally {
      setFolderProgress(null);
      if (folderInputRef.current) folderInputRef.current.value = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, prefix]);

  const { onDragStart, onDragOver, onDrop } = useFileDragDrop(
    prefix,
    async (id, path) => { await move(id, path); load(prefix); reloadFolders(); },
    handleUploadFiles,
  );

  const handleUploaded = () => { load(prefix); reloadQuota(); };

  const handleDeleteConfirm = async (recursive: boolean) => {
    if (!deleteTarget) return;
    await remove(deleteTarget.id, recursive);
    reloadQuota();
    if (deleteTarget.is_directory) reloadFolders();   // 刪 folder → 樹同步
    setDeleteTarget(null);
  };

  const handleRenameConfirm = async (newName: string) => {
    if (!renameTarget) return;
    await rename(renameTarget.id, newName);
    if (renameTarget.is_directory) reloadFolders();   // 改 folder 名 → 樹同步
    setRenameTarget(null);
  };

  const handleCreateFolder = async (name: string) => {
    const path = prefix ? `${prefix}${name}` : name;
    await createFolder(path);
    reloadFolders();   // 新 folder → 樹同步
    setNewFolderOpen(false);
  };

  if (permLoading) return null;

  if (!canRead) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">{t('common.noPermission', '沒有檢視權限')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}
         onContextMenu={e => { if (e.target === e.currentTarget) openCtx(e, null); }}>
      {/* 左側導覽（含底部 QuotaBar + 子目錄樹）*/}
      <SideNav
        current={category}
        prefix={prefix}
        onChange={c => { setCategory(c); setPrefix(''); clear(); }}
        onPrefixChange={p => { setPrefix(p); clear(); }}
        files={allCategoryFolders}
        quota={quota}
      />

      {/* 右側主區 */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Toolbar
          canCreate={canCreate}
          canUpdate={canUpdate}
          readonly={readonly}
          canUp={!!prefix}
          view={view}
          selectedCount={selected.size}
          onUpload={() => uploadRef.current?.click()}
          onNewFolder={() => setNewFolderOpen(true)}
          onUp={() => {
            const parts = prefix.replace(/\/$/, '').split('/');
            parts.pop();
            setPrefix(parts.length ? parts.join('/') + '/' : '');
          }}
          onRefresh={() => load(prefix)}
          onViewChange={setView}
          onBatchDownload={handleBatchDownload}
          onBatchDelete={() => setBatchDeleteOpen(true)}
          onBatchMove={() => setBatchMoveOpen(true)}
          onWebdavSettings={() => setWebdavOpen(true)}
          webdavEnabled={webdavEnabled}
        />

        {canCreate && !readonly && (
          <Box sx={{ px: 2, py: 0.5, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: '1px solid #eee' }}>
            <button type="button" disabled={!!folderProgress}
              onClick={() => folderInputRef.current?.click()}
              style={{ padding: '4px 10px', border: '1px solid #1976d2', color: '#1976d2', background: 'none', borderRadius: 4, cursor: folderProgress ? 'default' : 'pointer', fontSize: 13 }}>
              📁 {t('myDrive.uploadFolder', '上傳資料夾')}
            </button>
            <button type="button" onClick={() => setNewOfficeOpen(true)}
              style={{ padding: '4px 10px', border: '1px solid #2e7d32', color: '#2e7d32', background: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}>
              📄 {t('myDrive.newOfficeFile', '新增 Office 檔案')}
            </button>
            <input type="file" multiple hidden
              ref={(node) => {
                if (node) { node.setAttribute('webkitdirectory', ''); node.setAttribute('directory', ''); }
                folderInputRef.current = node;
              }}
              onChange={(e) => { handleFolderUpload(e.target.files); }} />
            {folderProgress && (
              <Typography variant="caption" color="text.secondary">
                {t('myDrive.folderUploading', { done: folderProgress.done, total: folderProgress.total, defaultValue: '上傳中 {{done}}/{{total}}' })}
              </Typography>
            )}
            {!folderProgress && folderSummary && (
              <Typography variant="caption" sx={{ color: folderSummary.fail > 0 ? '#d97706' : '#2e7d32' }}>
                {t('myDrive.folderUploadDone', { ok: folderSummary.ok, fail: folderSummary.fail, defaultValue: '上傳完成 · 成功 {{ok}} / 失敗 {{fail}}' })}
              </Typography>
            )}
          </Box>
        )}

        <AddressBar category={category} prefix={prefix} onNavigate={setPrefix} />

        <FileBrowser
          files={directFiles}
          loading={loading}
          view={view}
          selected={selected}
          readonly={readonly || !canDelete}
          onItemClick={onItemClick}
          onDoubleClick={handleDoubleClick}
          onContextMenu={openCtx}
          onPreview={handlePreview}
          onDownload={handleDownload}
          onDelete={setDeleteTarget}
          onDragStart={onDragStart}
          onDragOver={onDragOver}
          onDrop={onDrop}
        />
      </Box>

      {/* Hidden uploader */}
      <Box sx={{ display: 'none' }}>
        <FileUploader
          key={uploaderKey}
          dir_category={category}
          prefix={prefix}
          onUploaded={handleUploaded}
          disabled={quota ? quota.used_pct >= 100 : false}
          inputRef={uploadRef}
        />
      </Box>

      {/* 批次操作 dialogs */}
      <BatchDeleteDialog
        open={batchDeleteOpen}
        files={selectedFiles}
        onClose={() => setBatchDeleteOpen(false)}
        onConfirm={handleBatchDelete}
      />
      <FolderTreePicker
        open={batchMoveOpen}
        onClose={() => setBatchMoveOpen(false)}
        onPick={handleBatchMove}
        dirCategory={category}
        excludeFileId={0}
      />

      <WebDAVSettingsDialog
        open={webdavOpen}
        onClose={() => setWebdavOpen(false)}
        canUpdate={canUpdate}
        onStatusChange={refreshWebdavStatus}
      />

      {/* 對話框 */}
      <NewOfficeFileDialog open={newOfficeOpen} onConfirm={handleCreateOfficeFile}
        onClose={() => setNewOfficeOpen(false)} />

      <NewFolderDialog open={newFolderOpen} onConfirm={handleCreateFolder}
                       onClose={() => setNewFolderOpen(false)} />
      <RenameDialog open={!!renameTarget} file={renameTarget}
                    onConfirm={handleRenameConfirm} onClose={() => setRenameTarget(null)} />
      <DeleteConfirmDialog open={!!deleteTarget} file={deleteTarget}
                           onConfirm={handleDeleteConfirm} onClose={() => setDeleteTarget(null)} />
      <FilePreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />

      {shareTarget && (
        <ShareDialog
          open={!!shareTarget}
          onClose={() => setShareTarget(null)}
          sourceKind="my_drive"
          sourceId={shareTarget.id}
          filename={shareTarget.filename}
          resourceKind={shareTarget.is_directory ? 'folder' : 'file'}
        />
      )}

      <FolderTreePicker
        open={!!moveTarget}
        onClose={() => setMoveTarget(null)}
        onPick={async (targetFolderPath) => {
          if (!moveTarget) return;
          const newPath = targetFolderPath
            ? `${targetFolderPath}/${moveTarget.filename}`
            : moveTarget.filename;
          try {
            await myDriveService.moveFile(moveTarget.id, newPath);
            showSuccess('SYS330007', { name: moveTarget.filename });
            setMoveTarget(null);
            load(prefix);
            reloadQuota();
          } catch (err) {
            showApiError(err);
          }
        }}
        dirCategory={moveTarget?.dir_category ?? 'personal'}
        excludeFileId={moveTarget?.id ?? 0}
      />

      <FileContextMenu
        open={!!ctxPos}
        anchorPos={ctxPos}
        file={ctxFile}
        readonly={readonly || !canDelete}
        canShare={canShare}
        onAction={handleCtxAction}
        onClose={() => { setCtxPos(null); setCtxFile(null); }}
      />
    </Box>
  );
}
